import { useState } from "react"
import { cn } from "@/lib/cn"
import { toneCss } from "../format"
import type { ActionPayload } from "../types"

type Props = {
  value: ActionPayload | null | undefined
  onAction: (action: ActionPayload, refresh?: string[]) => Promise<unknown>
}

// Compact action button used inside table rows (e.g. "Reward!" on the
// Milestones table). Confirmation modal if the partner sets `confirm`.
// Tap target is ≥ 32×32 px even when the table is dense; full-width
// `action_row` buttons get the 44×44 minimum elsewhere.
export function ActionCell({ value, onAction }: Props) {
  const [busy, setBusy] = useState(false)
  if (!value || typeof value !== "object" || !value.label || !value.path || !value.method) {
    return <span className="text-app-subtle">—</span>
  }
  const css = toneCss(value.tone)

  async function click() {
    if (busy) return
    if (!value || !value.label || !value.path || !value.method) return
    if (value.confirm && !window.confirm(value.confirm)) return
    setBusy(true)
    try {
      await onAction(value)
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      className={cn(
        "inline-flex items-center gap-1 rounded-control border px-2 py-0.5 text-xs font-medium transition",
        css.text,
        css.bg,
        css.border,
        "hover:brightness-110 disabled:cursor-wait disabled:opacity-60",
      )}
    >
      {busy ? "…" : value.label}
    </button>
  )
}
